import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImageDown, Loader2, Wand2, Upload, Move, Type } from "lucide-react";

const RED = "#e4121b";
const WHITE = "#ffffff";
const BLACK = "#0a0a0a";

const slugify = (s) =>
  (s || "ja-tak").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "ja-tak";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export function JatakImageMaker({ imageUrl, defaultName = "", trigger }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName);
  const [price, setPrice] = useState("");
  const [imgSrc, setImgSrc] = useState("");
  const [loadingImg, setLoadingImg] = useState(false);
  // each line has its own top-left position (fraction of image)
  const [posTitle, setPosTitle] = useState({ x: 0.045, y: 0.34 });
  const [posPrice, setPosPrice] = useState({ x: 0.045, y: 0.43 });
  const [posExtra, setPosExtra] = useState({ x: 0.045, y: 0.52 });
  const [extra, setExtra] = useState("");
  const [scaleTitle, setScaleTitle] = useState(1);
  const [scalePrice, setScalePrice] = useState(1);
  const [scaleExtra, setScaleExtra] = useState(1);

  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const objUrlRef = useRef(null);
  const rectsRef = useRef({ title: null, price: null });
  const dragRef = useRef(null); // { line: 'title'|'price', ox, oy }

  const loadFromUrl = useCallback(async (url) => {
    setLoadingImg(true);
    try {
      const res = await api.get("/jatak/facebook/image-proxy", { params: { url }, responseType: "blob" });
      if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
      const obj = URL.createObjectURL(res.data);
      objUrlRef.current = obj;
      setImgSrc(obj);
    } catch {
      toast.error("Kunne ikke hente billedet fra Facebook");
    } finally {
      setLoadingImg(false);
    }
  }, []);

  useEffect(() => {
    if (open && imageUrl && !imgSrc) loadFromUrl(imageUrl);
  }, [open, imageUrl, imgSrc, loadFromUrl]);

  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
    const obj = URL.createObjectURL(f);
    objUrlRef.current = obj;
    setImgSrc(obj);
  };

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.complete || !img.naturalWidth) return;
    try { await document.fonts.load("64px Anton"); } catch { /* ignore */ }

    const W = img.naturalWidth, H = img.naturalHeight;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, W, H);

    const family = "Anton, 'Arial Black', sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.lineJoin = "round";
    ctx.miterLimit = 2;

    const titleText = (name || "").toUpperCase();
    const priceText = price ? `JA TAK PRIS ${price}` : "";
    const extraText = (extra || "").toUpperCase();
    const titleSize = Math.round(W * 0.064 * scaleTitle);
    const priceSize = Math.round(W * 0.075 * scalePrice);
    const extraSize = Math.round(W * 0.05 * scaleExtra);

    rectsRef.current = { title: null, price: null, extra: null };

    if (titleText) {
      const x = posTitle.x * W, y = posTitle.y * H;
      ctx.font = `${titleSize}px ${family}`;
      ctx.lineWidth = Math.max(2, titleSize * 0.16);
      ctx.strokeStyle = BLACK;
      ctx.strokeText(titleText, x, y);
      ctx.fillStyle = WHITE;
      ctx.fillText(titleText, x, y);
      rectsRef.current.title = { x, y, w: ctx.measureText(titleText).width, h: titleSize * 1.08 };
    }
    if (priceText) {
      const x = posPrice.x * W, y = posPrice.y * H;
      ctx.font = `${priceSize}px ${family}`;
      ctx.lineWidth = Math.max(2, priceSize * 0.17);
      ctx.strokeStyle = WHITE;
      ctx.strokeText(priceText, x, y);
      ctx.fillStyle = RED;
      ctx.fillText(priceText, x, y);
      rectsRef.current.price = { x, y, w: ctx.measureText(priceText).width, h: priceSize * 1.05 };
    }
    if (extraText) {
      const x = posExtra.x * W, y = posExtra.y * H;
      ctx.font = `${extraSize}px ${family}`;
      ctx.lineWidth = Math.max(2, extraSize * 0.16);
      ctx.strokeStyle = BLACK;
      ctx.strokeText(extraText, x, y);
      ctx.fillStyle = WHITE;
      ctx.fillText(extraText, x, y);
      rectsRef.current.extra = { x, y, w: ctx.measureText(extraText).width, h: extraSize * 1.08 };
    }
  }, [name, price, extra, posTitle, posPrice, posExtra, scaleTitle, scalePrice, scaleExtra]);

  const onImgLoad = () => { draw(); };
  useEffect(() => { draw(); }, [draw, imgSrc]);

  const toImgCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const cy = (e.clientY - rect.top) * (canvas.height / rect.height);
    return { cx, cy };
  };

  const hitLine = (cx, cy) => {
    const canvas = canvasRef.current;
    const pad = canvas.width * 0.025;
    const inside = (r) => r && cx >= r.x - pad && cx <= r.x + r.w + pad && cy >= r.y - pad && cy <= r.y + r.h + pad;
    // check in reverse draw order so the topmost line wins on overlap
    if (inside(rectsRef.current.extra)) return "extra";
    if (inside(rectsRef.current.price)) return "price";
    if (inside(rectsRef.current.title)) return "title";
    return null;
  };

  const onPointerDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { cx, cy } = toImgCoords(e);
    const line = hitLine(cx, cy);
    if (!line) return;
    const r = rectsRef.current[line];
    dragRef.current = { line, ox: cx - r.x, oy: cy - r.y };
    canvas.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const canvas = canvasRef.current;
    const { cx, cy } = toImgCoords(e);
    const r = rectsRef.current[drag.line];
    if (!r) return;
    const nx = clamp((cx - drag.ox) / canvas.width, 0, 1 - r.w / canvas.width);
    const ny = clamp((cy - drag.oy) / canvas.height, 0, 1 - r.h / canvas.height);
    const setter = { title: setPosTitle, price: setPosPrice, extra: setPosExtra }[drag.line];
    setter({ x: nx, y: ny });
  };

  const onPointerUp = (e) => {
    if (dragRef.current) { canvasRef.current?.releasePointerCapture?.(e.pointerId); dragRef.current = null; }
  };

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgSrc) { toast.error("Vælg et billede først"); return; }
    canvas.toBlob((blob) => {
      if (!blob) { toast.error("Kunne ikke lave billedet"); return; }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ja-tak-${slugify(name)}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Billede hentet");
    }, "image/png");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="h-8 rounded-lg gap-1.5 text-xs" data-testid="open-image-maker">
            <Wand2 className="w-3.5 h-3.5" />Lav billede
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="image-maker-dialog">
        <DialogHeader>
          <DialogTitle>Lav Ja tak-billede</DialogTitle>
          <DialogDescription>Skriv produktnavn og pris, træk hver linje på plads og justér størrelsen — hent så billedet som PNG.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="im-name" className="text-xs text-slate-500">Produktnavn</Label>
              <Input id="im-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="LAKRIDS MIX – 800 GR." data-testid="image-maker-name" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="im-price" className="text-xs text-slate-500">Ja tak pris</Label>
              <Input id="im-price" value={price} onChange={(e) => setPrice(e.target.value)}
                placeholder="49,99" data-testid="image-maker-price" className="mt-1" />
            </div>
          </div>

          <div>
            <Label htmlFor="im-extra" className="text-xs text-slate-500">Ekstra tekst (valgfri)</Label>
            <Input id="im-extra" value={extra} onChange={(e) => setExtra(e.target.value)}
              placeholder="Fx SPAR 20 KR · KUN I AFTEN" data-testid="image-maker-extra" className="mt-1" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-3">
              <Label className="text-xs text-slate-500 inline-flex items-center gap-1.5 shrink-0"><Type className="w-3.5 h-3.5" />Navn</Label>
              <Slider value={[scaleTitle]} min={0.5} max={2} step={0.05}
                onValueChange={(v) => setScaleTitle(v[0])} className="flex-1" data-testid="image-maker-size-title" />
              <span className="text-xs text-slate-500 w-10 text-right tabular-nums">{Math.round(scaleTitle * 100)}%</span>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-xs text-slate-500 inline-flex items-center gap-1.5 shrink-0"><Type className="w-3.5 h-3.5" />Pris</Label>
              <Slider value={[scalePrice]} min={0.5} max={2} step={0.05}
                onValueChange={(v) => setScalePrice(v[0])} className="flex-1" data-testid="image-maker-size-price" />
              <span className="text-xs text-slate-500 w-10 text-right tabular-nums">{Math.round(scalePrice * 100)}%</span>
            </div>
            {extra && (
              <div className="flex items-center gap-3">
                <Label className="text-xs text-slate-500 inline-flex items-center gap-1.5 shrink-0"><Type className="w-3.5 h-3.5" />Ekstra</Label>
                <Slider value={[scaleExtra]} min={0.5} max={2} step={0.05}
                  onValueChange={(v) => setScaleExtra(v[0])} className="flex-1" data-testid="image-maker-size-extra" />
                <span className="text-xs text-slate-500 w-10 text-right tabular-nums">{Math.round(scaleExtra * 100)}%</span>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] overflow-hidden flex items-center justify-center min-h-[240px] relative">
            {loadingImg ? (
              <span className="text-sm text-slate-500 py-10 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Henter billede…</span>
            ) : imgSrc ? (
              <>
                <canvas ref={canvasRef} className="max-w-full h-auto block touch-none cursor-move"
                  onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
                  data-testid="image-maker-canvas" />
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-black/55 text-white pointer-events-none">
                  <Move className="w-3 h-3" />Træk hver linje for at flytte den
                </span>
              </>
            ) : (
              <span className="text-sm text-slate-500 py-10">Intet billede valgt endnu</span>
            )}
            {imgSrc && <img ref={imgRef} src={imgSrc} alt="" crossOrigin="anonymous" onLoad={onImgLoad} className="hidden" />}
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium border border-[#e2e8f0] bg-white text-slate-600 hover:text-slate-800 cursor-pointer" data-testid="image-maker-upload">
              <Upload className="w-4 h-4" />Brug eget billede
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
            </label>
            <Button onClick={download} disabled={!imgSrc} className="h-9 rounded-lg gap-1.5" data-testid="image-maker-download">
              <ImageDown className="w-4 h-4" />Hent billede
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
